import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { visuallyHidden } from "@mui/utils";
import FieldHelper from "../helpers/FieldHelper";
import { CsvBuilder } from "filefy";
import { Button } from "@mui/material";
import { FileDownload } from "@mui/icons-material";

interface Data {
    filename: string;
    method: string,
    mode: string,
    trigtime: number;
    trigutc: number;
    detectors: string;
    n0: number;
    n1: number;
    n2: number;
    n3: number;
    n4: number;
    n5: number;
    n6: number;
    n7: number;
    n8: number;
    n9: number;
    na: number;
    nb: number;
    duration: number;
    hr_25: number;
    hr_50: number;
    hr_75: number;
    hr_300: number;
    trig_grade: number,
    sfl_grade: number,
    transnt_grade: number,
    par_grade: number,
    trig_id: string;
    sfl_id: string;
    transnt_id: string;
    sgr: number,
    sgrb: number,
    lgrb: number,
    sflare: number,
    tgf: number,
    transnt: number,
    loclpar: number,
    distpar: number,
    no_evaluation: number,
}
//
// function createData(name: string, calories: number, fat: number, carbs: number, protein: number): Data {
//     return {
//         name,
//         calories,
//         fat,
//         carbs,
//         protein,
//     };
// }
//
// const rows = [
//     createData("Cupcake", 305, 3.7, 67, 4.3),
//     createData("Donut", 452, 25.0, 51, 4.9),
//     createData("Eclair", 262, 16.0, 24, 6.0),
//     createData("Frozen yoghurt", 159, 6.0, 24, 4.0),
//     createData("Gingerbread", 356, 16.0, 49, 3.9),
//     createData("Honeycomb", 408, 3.2, 87, 6.5),
//     createData("Ice cream sandwich", 237, 9.0, 37, 4.3),
//     createData("Jelly Bean", 375, 0.0, 94, 0.0),
//     createData("KitKat", 518, 26.0, 65, 7.0),
//     createData("Lollipop", 392, 0.2, 98, 0.0),
//     createData("Marshmallow", 318, 0, 81, 2.0),
//     createData("Nougat", 360, 19.0, 9, 37.0),
//     createData("Oreo", 437, 18.0, 63, 4.0),
// ];

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(order: Order, orderBy: Key): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
    return order === "desc" ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
    id: keyof Data;
    label: string;
    numeric: boolean;
}

const fh = new FieldHelper();

const headCells: readonly HeadCell[] = [
    {
        id: "filename",
        numeric: false,
        label: fh.getLabel("filename"),
    },
    {
        id: "trigtime",
        numeric: true,
        label: fh.getLabel("trigtime"),
    },
    {
        id: "trigutc",
        numeric: true,
        label: fh.getLabel("trigutc"),
    },
    {
        id: "detectors",
        numeric: false,
        label: fh.getLabel("detectors"),
    },
    {
        id: "n0",
        numeric: true,
        label: fh.getLabel("n0"),
    },
    {
        id: "n1",
        numeric: true,
        label: fh.getLabel("n1"),
    },
    {
        id: "n2",
        numeric: true,
        label: fh.getLabel("n2"),
    },
    {
        id: "n3",
        numeric: true,
        label: fh.getLabel("n3"),
    },
    {
        id: "n4",
        numeric: true,
        label: fh.getLabel("n4"),
    },
    {
        id: "n5",
        numeric: true,
        label: fh.getLabel("n5"),
    },
    {
        id: "n6",
        numeric: true,
        label: fh.getLabel("n6"),
    },
    {
        id: "n7",
        numeric: true,
        label: fh.getLabel("n7"),
    },
    {
        id: "n8",
        numeric: true,
        label: fh.getLabel("n8"),
    },
    {
        id: "n9",
        numeric: true,
        label: fh.getLabel("n9"),
    },
    {
        id: "na",
        numeric: true,
        label: fh.getLabel("na"),
    },
    {
        id: "nb",
        numeric: true,
        label: fh.getLabel("nb"),
    },
    {
        id: "duration",
        numeric: true,
        label: "Duration",
    },
    {
        id: "hr_25",
        numeric: true,
        label: fh.getLabel("hr_25"),
    },
    {
        id: "hr_50",
        numeric: true,
        label: fh.getLabel("hr_50"),
    },
    {
        id: "hr_75",
        numeric: true,
        label: fh.getLabel("hr_75"),
    },
    {
        id: "hr_300",
        numeric: true,
        label: fh.getLabel("hr_300"),
    },
    {
        id: "trig_grade",
        numeric: true,
        label: fh.getLabel("trig_gr"),
    },
    {
        id: "sfl_grade",
        numeric: true,
        label: fh.getLabel("sfl_gr"),
    },
    {
        id: "transnt_grade",
        numeric: true,
        label: fh.getLabel("transnt_gr"),
    },
    {
        id: "par_grade",
        numeric: true,
        label: fh.getLabel("par_gr"),
    },
    {
        id: "trig_id",
        numeric: false,
        label: fh.getLabel("trig_id"),
    },
    {
        id: "sfl_id",
        numeric: false,
        label: fh.getLabel("sfl_id"),
    },
    {
        id: "transnt_id",
        numeric: false,
        label: fh.getLabel("transnt_id"),
    },
    {
        id: "sgr",
        numeric: true,
        label: fh.getLabel("sgr_prob"),
    },
    {
        id: "sgrb",
        numeric: true,
        label: fh.getLabel("sgrb_prob"),
    },
    {
        id: "lgrb",
        numeric: true,
        label: fh.getLabel("lgrb_prob"),
    },
    {
        id: "sflare",
        numeric: true,
        label: fh.getLabel("sfl_prob"),
    },
    {
        id: "tgf",
        numeric: true,
        label: fh.getLabel("tgf_prob"),
    },
    {
        id: "transnt",
        numeric: true,
        label: fh.getLabel("transnt_prob"),
    },
    {
        id: "loclpar",
        numeric: true,
        label: fh.getLabel("lpar_prob"),
    },
    {
        id: "distpar",
        numeric: true,
        label: fh.getLabel("dpar_prob"),
    },
    {
        id: "no_evaluation",
        numeric: true,
        label: fh.getLabel("no_evaluation"),
    },
]

const headCellsCsv: readonly HeadCell[] = [
    {
        id: "filename",
        numeric: false,
        label: fh.getLabel("filename"),
    },
    {
        id: "method",
        numeric: false,
        label: fh.getLabel("method"),
    },
    {
        id: "mode",
        numeric: false,
        label: fh.getLabel("mode"),
    },
    {
        id: "trigtime",
        numeric: true,
        label: fh.getLabel("trigtime"),
    },
    {
        id: "trigutc",
        numeric: true,
        label: fh.getLabel("trigutc"),
    },
    {
        id: "detectors",
        numeric: false,
        label: fh.getLabel("detectors"),
    },
    {
        id: "n0",
        numeric: true,
        label: fh.getLabel("n0"),
    },
    {
        id: "n1",
        numeric: true,
        label: fh.getLabel("n1"),
    },
    {
        id: "n2",
        numeric: true,
        label: fh.getLabel("n2"),
    },
    {
        id: "n3",
        numeric: true,
        label: fh.getLabel("n3"),
    },
    {
        id: "n4",
        numeric: true,
        label: fh.getLabel("n4"),
    },
    {
        id: "n5",
        numeric: true,
        label: fh.getLabel("n5"),
    },
    {
        id: "n6",
        numeric: true,
        label: fh.getLabel("n6"),
    },
    {
        id: "n7",
        numeric: true,
        label: fh.getLabel("n7"),
    },
    {
        id: "n8",
        numeric: true,
        label: fh.getLabel("n8"),
    },
    {
        id: "n9",
        numeric: true,
        label: fh.getLabel("n9"),
    },
    {
        id: "na",
        numeric: true,
        label: fh.getLabel("na"),
    },
    {
        id: "nb",
        numeric: true,
        label: fh.getLabel("nb"),
    },
    {
        id: "duration",
        numeric: true,
        label: "Duration",
    },
    {
        id: "hr_25",
        numeric: true,
        label: fh.getLabel("hr_25"),
    },
    {
        id: "hr_50",
        numeric: true,
        label: fh.getLabel("hr_50"),
    },
    {
        id: "hr_75",
        numeric: true,
        label: fh.getLabel("hr_75"),
    },
    {
        id: "hr_300",
        numeric: true,
        label: fh.getLabel("hr_300"),
    },
    {
        id: "trig_grade",
        numeric: true,
        label: fh.getLabel("trig_gr"),
    },
    {
        id: "sfl_grade",
        numeric: true,
        label: fh.getLabel("sfl_gr"),
    },
    {
        id: "transnt_grade",
        numeric: true,
        label: fh.getLabel("transnt_gr"),
    },
    {
        id: "par_grade",
        numeric: true,
        label: fh.getLabel("par_gr"),
    },
    {
        id: "trig_id",
        numeric: false,
        label: fh.getLabel("trig_id"),
    },
    {
        id: "sfl_id",
        numeric: false,
        label: fh.getLabel("sfl_id"),
    },
    {
        id: "transnt_id",
        numeric: false,
        label: fh.getLabel("transnt_id"),
    },
    {
        id: "sgr",
        numeric: true,
        label: fh.getLabel("sgr_prob"),
    },
    {
        id: "sgrb",
        numeric: true,
        label: fh.getLabel("sgrb_prob"),
    },
    {
        id: "lgrb",
        numeric: true,
        label: fh.getLabel("lgrb_prob"),
    },
    {
        id: "sflare",
        numeric: true,
        label: fh.getLabel("sfl_prob"),
    },
    {
        id: "tgf",
        numeric: true,
        label: fh.getLabel("tgf_prob"),
    },
    {
        id: "transnt",
        numeric: true,
        label: fh.getLabel("transnt_prob"),
    },
    {
        id: "loclpar",
        numeric: true,
        label: fh.getLabel("lpar_prob"),
    },
    {
        id: "distpar",
        numeric: true,
        label: fh.getLabel("dpar_prob"),
    },
    {
        id: "no_evaluation",
        numeric: true,
        label: fh.getLabel("no_evaluation"),
    },
];

interface EnhancedTableHeadProps {
    onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
    order: Order;
    orderBy: string;
}

function EnhancedTableHead(props: EnhancedTableHeadProps) {
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell key={headCell.id} align={headCell.numeric ? "right" : "left"} sortDirection={orderBy === headCell.id ? order : false}>
                        <TableSortLabel active={orderBy === headCell.id} direction={orderBy === headCell.id ? order : "asc"} onClick={createSortHandler(headCell.id)}>
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

const EnhancedTableToolbar = (props: any) => {
    return (
        <Toolbar
            sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
            }}>
            <Typography sx={{ flex: "1 1 100%" }} variant="h6" id="tableTitle" component="div">
                Search Results
            </Typography>
            <Button variant="outlined" onClick={props.onExport} endIcon={<FileDownload />}></Button>
        </Toolbar>
    );
};

interface EnhancedTableProps {
    onSelect: any;
    rows: any[];
    setPage: (page: number) => void;
    setRowsPerPage: (rows: number) => void;
    page: number;
    rowsPerPage: number;
    count: number;
    onDownloadCsv: () => Promise<any[]>;
}

export default function EnhancedTable(props: EnhancedTableProps) {
    const { rows, page, rowsPerPage, onSelect, count, onDownloadCsv } = props;

    const [order, setOrder] = React.useState<Order>("asc");
    const [orderBy, setOrderBy] = React.useState<keyof Data>("filename");


    function setPage(page: number) {
        props.setPage(page);
    }

    function setRowsPerPage(rows: number) {
        props.setRowsPerPage(rows);
    }

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value));
        setPage(0);
    };

    const exportCSV = async () => {

        let data = await onDownloadCsv();
        const columns = headCellsCsv.map(cell=> {
            return cell.label
        });
        const rowies = data.map(row => {console.log (row); return Object.values(row) as string[]});
        
        const builder = new CsvBuilder("gbm_search_results.csv");
        builder
            .setDelimeter(",")
            .setColumns(columns)
            .addRows(rowies)
            .exportFile();
    };

    // Avoid a layout jump when reaching the last page with empty rows.
    // const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

    return (
        <Box sx={{ width: "100%" }}>
            <Paper sx={{ width: "100%", mb: 2 }}>
                <TableContainer>
                    <EnhancedTableToolbar onExport={exportCSV} />
                    <TablePagination rowsPerPageOptions={[100, 150, 200]} component="div" count={Number(count)} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
                    <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
                        <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                        <TableBody>
                            {/* if you don't need to support IE11, you can replace the `stableSort` call with:
              rows.slice().sort(getComparator(order, orderBy)) */}
                            {rows
                                .slice()
                                .sort(getComparator(order, orderBy))
                                .map((row, index) => {
                                    const labelId = `enhanced-table-checkbox-${index}`;

                                    return (
                                        <TableRow hover tabIndex={-1} key={row.filename || row.FILENAME} sx={{ cursor: "pointer" }} onClick={() => onSelect(row)}>
                                            <TableCell component="th" id={labelId} scope="row">
                                                {row.filename || row.FILENAME}
                                            </TableCell>
                                            <TableCell align="right">{row.trigtime || row.TRIGTIME}</TableCell>
                                            <TableCell align="right">{row.trigutc || row.TRIGUTC}</TableCell>
                                            <TableCell align="right">{row.detectors || row.Detectors}</TableCell>
                                            <TableCell align="right">{row.n0 || row.N0}</TableCell>
                                            <TableCell align="right">{row.n1 || row.N1}</TableCell>
                                            <TableCell align="right">{row.n2 || row.N2}</TableCell>
                                            <TableCell align="right">{row.n3 || row.N3}</TableCell>
                                            <TableCell align="right">{row.n4 || row.N4}</TableCell>
                                            <TableCell align="right">{row.n5 || row.N5}</TableCell>
                                            <TableCell align="right">{row.n6 || row.N6}</TableCell>
                                            <TableCell align="right">{row.n7 || row.N7}</TableCell>
                                            <TableCell align="right">{row.n8 || row.N8}</TableCell>
                                            <TableCell align="right">{row.n9 || row.N9}</TableCell>
                                            <TableCell align="right">{row.na || row.Na}</TableCell>
                                            <TableCell align="right">{row.nb || row.Nb}</TableCell>
                                            <TableCell align="right">{row.duration || row.DURATION}</TableCell>
                                            <TableCell align="right">{row.hr_25 || row.HR_25}</TableCell>
                                            <TableCell align="right">{row.hr_50 || row.HR_50}</TableCell>
                                            <TableCell align="right">{row.hr_75 || row.HR_75}</TableCell>
                                            <TableCell align="right">{row.hr_300 || row.HR_300}</TableCell>
                                            <TableCell align="right">{row.trig_gr || row.TRIG_GR}</TableCell>
                                            <TableCell align="right">{row.sfl_gr || row.SFL_GR}</TableCell>
                                            <TableCell align="right">{row.transnt_gr || row.TRANSNT_GR}</TableCell>
                                            <TableCell align="right">{row.par_gr || row.PAR_GR}</TableCell>
                                            <TableCell align="right">{row.trig_id || row.TRIG_ID}</TableCell>
                                            <TableCell align="right">{row.sfl_id || row.SFL_ID}</TableCell>
                                            <TableCell align="right">{row.transnt_id || row.TRANSNT_ID}</TableCell>
                                            <TableCell align="right">{row.sgr_prob || row.SGR_Prob}</TableCell>
                                            <TableCell align="right">{row.sgrb_prob || row.SGRB_Prob}</TableCell>
                                            <TableCell align="right">{row.lgrb_prob || row.LGRB_Prob}</TableCell>
                                            <TableCell align="right">{row.sfl_prob || row.SFL_Prob}</TableCell>
                                            <TableCell align="right">{row.tgf_prob || row.TGF_Prob}</TableCell>
                                            <TableCell align="right">{row.transnt_prob || row.TRANSNT_Prob}</TableCell>
                                            <TableCell align="right">{row.lpar_prob || row.LPAR_Prob}</TableCell>
                                            <TableCell align="right">{row.dpar_prob || row.DPAR_Prob}</TableCell>
                                            <TableCell align="right">{row.no_evaluation || row.NO_EVALUATION}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            {/* {emptyRows > 0 && (
                                <TableRow
                                    style={{
                                        height: 33 * emptyRows,
                                    }}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )} */}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination rowsPerPageOptions={[100, 150, 200]} component="div" count={Number(count)} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
            </Paper>
        </Box>
    );
}
